import React, {useEffect, useState} from 'react';
import "./Wallet.scss";
import {RequestWallet} from "../../../request/RequestWallet";
import routes from "../../../utils/routes.json";
import {useNavigate} from "react-router-dom";
import {useTranslation} from 'react-i18next';
import {useNotification} from "../../../containers/NotificationContext/NotificationContext";
import LineChart from "../../../components/Charts/LineChart/LineChart";
import Skeleton from '@mui/material/Skeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import HistoriqueTable from "../../../containers/Table/HistoriqueTable/HistoriqueTable";
import "../../../containers/Table/HistoriqueTable/HistoriqueTable.scss";
import Camembert from "../../../components/Camembert/Camembert";
import CustomImage from "../../../components/CustomImage/CustomImage";
import constants from '../../../utils/constants.js'
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import {RequestAutomation} from "../../../request/RequestAutomation";
import demo_wallet from "../../../utils/demoData/demo_wallet.json";
import demo_history from "../../../utils/demoData/demo_history.json";
import demo_automation from "../../../utils/demoData/demo_automation.json";
import SwitchButton from "../../../components/Buttons/SwitchButton/SwitchButton";
import {getStockLogo} from "../../../utils/services";

const WalletSkeleton = () => (
    <div className="wallet-dashboard">
        <div className="wallet-grid">
            <div className="wallet-header premium-card">
                <div>
                    <div className="skeleton mb-2" style={{ width: 150, height: 32, borderRadius: 8 }}></div>
                    <div className="skeleton" style={{ width: 100, height: 16, borderRadius: 4 }}></div>
                </div>
                <div className="wallet-metrics">
                    <div className="skeleton stat-pill" style={{ height: 36, borderRadius: 8 }}></div>
                    <div className="skeleton stat-pill" style={{ height: 36, borderRadius: 8 }}></div>
                    <div className="skeleton stat-pill" style={{ height: 36, borderRadius: 8 }}></div>
                </div>
            </div>
            <div className="wallet-chart-container premium-card">
                <div className="skeleton w-100 h-100" style={{ borderRadius: 12, minHeight: 250 }}></div>
            </div>
            
            <div className="list-card premium-card list-stocks">
                <div className="skeleton mb-3" style={{ width: 120, height: 24, borderRadius: 4 }}></div>
                {[...Array(6)].map((_, i) => (
                    <div key={i} className="d-flex align-center mb-3" style={{ gap: 12 }}>
                        <div className="skeleton" style={{ width: 36, height: 36, borderRadius: '50%', flexShrink: 0 }}></div>
                        <div className="w-100">
                            <div className="skeleton mb-2" style={{ width: '60%', height: 16, borderRadius: 4 }}></div>
                            <div className="skeleton" style={{ width: '40%', height: 12, borderRadius: 4 }}></div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="list-card premium-card list-favorites">
                <div className="skeleton mb-3" style={{ width: 120, height: 24, borderRadius: 4 }}></div>
                <div className="list-scrollable">
                    {[...Array(4)].map((_, i) => (
                        <div key={i} className="d-flex align-center p-2" style={{ gap: 12, border: '1px solid #e2e8f0', borderRadius: 12 }}>
                            <div className="skeleton" style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0 }}></div>
                            <div className="skeleton" style={{ width: '50%', height: 16, borderRadius: 4 }}></div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="list-card premium-card list-orders">
                <div className="skeleton mb-3" style={{ width: 120, height: 24, borderRadius: 4 }}></div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    {[...Array(2)].map((_, i) => (
                        <div key={i} className="skeleton" style={{ height: 100, borderRadius: 12 }}></div>
                    ))}
                </div>
            </div>
        </div>
        
        <div className="premium-card">
             <div className="skeleton mb-3" style={{ width: 150, height: 30, borderRadius: 8 }}></div>
             <div className="skeleton w-100" style={{ height: 200, borderRadius: 12 }}></div>
        </div>
    </div>
);

const HistoriqueSkeletonTable = () => {
    const { t } = useTranslation();
    return (
        <div className="historique-table">
            <Paper sx={{ width: '100%', overflow: 'hidden', boxShadow: 'none', background: 'transparent', border: 'none' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0px', flexWrap: 'wrap', gap: '16px' }}>
                    <div className="skeleton text" style={{ width: 150, height: 28, borderRadius: 4 }}></div>
                    <div style={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
                         <div className="skeleton text" style={{ width: '100%', maxWidth: 350, height: 40, borderRadius: 20 }}></div>
                    </div>
                </div>
                <TableContainer sx={{ borderRadius: '0' }}>
                    <Table sx={{ minWidth: 0 }} aria-label="skeleton table">
                        <TableHead>
                            <TableRow>
                                <TableCell className="tableHeader"><div className="skeleton text" style={{ width: 60, height: 16 }}></div></TableCell>
                                <TableCell className="tableHeader"><div className="skeleton text" style={{ width: 60, height: 16 }}></div></TableCell>
                                <TableCell className="tableHeader"><div className="skeleton text" style={{ width: 60, height: 16 }}></div></TableCell>
                                <TableCell className="tableHeader"><div className="skeleton text" style={{ width: 60, height: 16 }}></div></TableCell>
                                <TableCell className="tableHeader"><div className="skeleton text" style={{ width: 60, height: 16 }}></div></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {[1, 2, 3].map(i => (
                                <TableRow key={i} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                    <TableCell data-label={t('wallet.table.ticker')} className="ticker tableCell" component="th" scope="row">
                                        <div className="history-logo-container">
                                            <div className="skeleton logo" style={{ width: 36, height: 36, borderRadius: '50%' }}></div>
                                        </div>
                                        <span className="ticker-text" style={{ display: 'inline-block', width: '50%' }}>
                                            <div className="skeleton text" style={{ width: '100%', height: 24, borderRadius: 4 }}></div>
                                        </span>
                                    </TableCell>
                                    <TableCell data-label={t('wallet.table.quantity')} className="tableCell quantity">
                                        <div className="skeleton text" style={{ display: 'inline-block', width: 40, height: 20, borderRadius: 4 }}></div>
                                    </TableCell>
                                    <TableCell data-label={t('wallet.table.price')} className="tableCell price">
                                        <div className="skeleton text" style={{ display: 'inline-block', width: 50, height: 20, borderRadius: 4 }}></div>
                                    </TableCell>
                                    <TableCell data-label={t('wallet.table.date')} className="tableCell date">
                                        <div className="skeleton text" style={{ display: 'inline-block', width: 70, height: 20, borderRadius: 4 }}></div>
                                    </TableCell>
                                    <TableCell data-label={t('wallet.table.type')} className="tableCell type">
                                        <span className="type-pill" style={{ display: 'inline-block', padding: '0', border: 'none' }}>
                                            <div className="skeleton pill" style={{ width: 50, height: 24, borderRadius: 9999 }}></div>
                                        </span>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </div>
    );
};

function Wallet({demo = false}) {
    const {t} = useTranslation();
    const notificationService = useNotification();
    const [data, setData] = useState(null);
    const requestWallet = new RequestWallet();
    const requestAutomation = new RequestAutomation();
    const navigate = useNavigate();
    const [isPercent, setIsPercent] = useState();
    const [dataGraph, setDataGraph] = useState(null);
    const [dateGraph, setDateGraph] = useState(null);
    const [dataCam, setDataCam] = useState(null)
    const [isLoading, setIsLoading] = useState(true)
    const [historique, setHistorique] = useState([]);
    const [switchBtn, setSwitchBtn] = useState(true);
    const [favsTickers, setFavsTickers] = useState([])
    const [dataAutomation, setDataAutomation] = useState([]);
    const transactionTypes = {
        BUY: t('transactionWidget.buy'),
        SELL: t('transactionWidget.sell')
    };

    useEffect(() => {
        const baliseHeader = document.getElementById("header");
        baliseHeader.classList.remove('h-home');
        loadData();
    }, [])

    async function loadData() {
        setIsLoading(true);
        await Promise.all([
            initWallet(),
            initHistorique(),
            initAutomation()
        ]);
        setIsLoading(false);
    }

    async function initWallet() {
        let resp;
        if (demo) {
            resp = demo_wallet;
        } else {
            resp = await requestWallet.getWallet();
        }

        if (resp.data && resp.data.favoris.length > 0) {
            setFavsTickers(resp.data.favoris.map(stock => stock.ticker))
        }
        resp.data.actions.sort((a, b) => {
            return b.performance.value - a.performance.value
        });
        setData(resp.data);
        await initGraph(resp.data);
        await initCamembert(resp.data);
    }

    async function initHistorique() {
        let resp;
        if (demo) {
            resp = demo_history;
        } else {
            await sleep(1000)
            resp = await requestWallet.getHistorique();
        }
        if(resp.data){
            setHistorique(resp.data)
        }
    }

    async function initAutomation(){
        let resp;
        if (demo) {
            resp = demo_automation
        } else {
            resp = await requestAutomation.getAutomation();
        }
        if (resp.data){
            let liste_automations = []
            const liste_aux = resp.data.automations
            liste_aux.map((item)=>{
                let order = {}
                order["Ticker"] = item.ticker
                order.id = item.id
                if (item.type && item.type.toUpperCase() === "PRICE_THRESHOLD") {
                    order[t("transactionWidget.orderType")] = t("transactionWidget.stopOrder")
                    if (item.transactionType && item.transactionType.toUpperCase() === "BUY") {
                        order[t("transactionWidget.transactionType")] = transactionTypes.BUY
                    } else {
                        order[t("transactionWidget.transactionType")] = transactionTypes.SELL
                    }
                    order[t('transactionWidget.quantity')] = item.quantity
                    order[t('transactionWidget.thresholdType')] = t(`transactionWidget.${item.thresholdType.toLowerCase()}`)
                    order[t('transactionWidget.stopPrice')] = item.thresholdPrice
                }else{
                    const isBuy = item.transactionType && item.transactionType.toUpperCase() === "BUY";
                    order[t("transactionWidget.orderType")] = isBuy ? t("transactionWidget.investmentPlanning_buy") : t("transactionWidget.investmentPlanning_sell");
                    if (isBuy) {
                        order[t("transactionWidget.transactionType")] = transactionTypes.BUY;
                    } else {
                        order[t("transactionWidget.transactionType")] = transactionTypes.SELL;
                    }
                    order[t('transactionWidget.quantity')] = item.quantity || item.buyQuantity || item.quantite || 0;
                    const freq = item.frequency || item.frequence || item.recurrence || '';
                    order[t('transactionWidget.frequency')] = freq ? t(`transactionWidget.${freq.toLowerCase()}`) : '';
                }
                liste_automations.push(order)
            })
            setDataAutomation(liste_automations);
        }
    }

    async function delAutomation(id) {
        if (demo) {
            notificationService.notify(t('demo.unavailable'), 'info');
            return;
        }
        if (window.confirm(t('wallet.confirmDeleteOrder'))) {
            await requestAutomation.deleteAutomation(id);
            await loadData();
        }
    }

    function sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }


    async function initGraph(resp) {
        let value = [];
        let date = [];
        if (resp.performanceHistory.length > 0) {
            resp.performanceHistory.forEach((item, index) => {
                value.push(item.value + constants.starting_money);
                date.push(DDMMYYYY(item.date));
            })
        } else {
            // Pour avoir une ligne, et pas un point lorsque l'utilisateur vient de s'inscrire
            value.push(resp.totalValue);
            date.push(getDate());
        }

        date.push(getDate())
        value.push(resp.totalValue)
        await setDateGraph(date);
        await setDataGraph(value)
    }

    async function initCamembert(resp) {
        let ticker = [];
        let percent = [];
        ticker.push(t('wallet.soldeCamembert'));
        percent.push(parseFloat((100 * resp.solde / resp.totalValue).toFixed(2)))
        resp.actions.forEach((item) => {
            ticker.push(item.ticker);
            const percentage = 100 * (item.price * item.quantity) / resp.totalValue;
            percent.push(parseFloat(percentage.toFixed(2)));
        })

        await setDataCam({
            "label": ticker,
            "data": percent,
        })
    }


    function goTicker(ticker) {
        navigate(`${routes.stock_nav}/${ticker}`)
    }


    function DDMMYYYY(date) {
        const dateToFormat = new Date(date);

        function addZero(number) {
            return number < 10 ? '0' + number : number;
        }

        const jour = addZero(dateToFormat.getDate());
        const mois = addZero(dateToFormat.getMonth() + 1);
        const année = dateToFormat.getFullYear();
        const dateFormatted = `${jour}/${mois}/${année}`;
        return dateFormatted
    }

    function getDate() {
        const currentDate = new Date();
        const day = currentDate.getDate().toString().padStart(2, '0');
        const month = (currentDate.getMonth() + 1).toString().padStart(2, '0');
        const year = currentDate.getFullYear();

        return `${day}/${month}/${year}`;
    }

    async function manageFav(ticker){
      if (!demo) {
        await requestWallet.delFavori(ticker)
        await loadData();
      }
    }
    return (
        <div className={`containerPage ${demo ? 'demo' : ''}`}>
            {
                isLoading || data === null ?
                    <WalletSkeleton/>
                    :
                    <div className="wallet-dashboard">
                        {demo &&
                            <div className="demoBanner">
                                <p className={"mt-0 mb-0"}>{t('demo.banner')}</p>
                            </div>
                        }

                        <div className="wallet-grid">
                            <div className="wallet-header premium-card animate-fade-up delay-1">
                                <div>
                                    <h1>{t('wallet.wallet')}</h1>
                                    <h3>{t('wallet.ranking', { ranking: data.rank })}</h3>
                                </div>
                                
                                <div className="wallet-metrics">
                                    <div className="stat-pill" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                        <span className="stat-label">Valeur Totale</span>
                                        <span className="stat-value">{data.totalValue.toFixed(2)} $</span>
                                    </div>
                                    <div className="stat-pill" style={{ 
                                        background: data.performance.value >= 0 ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                                        color: data.performance.value >= 0 ? '#10b981' : '#ef4444'
                                    }}>
                                        <span className="stat-label">Performance</span>
                                        <span className="stat-value">
                                            {data.performance.value >= 0 ? '+' : ''}{data.performance.value.toFixed(2)} $ ({data.performance.percentage})
                                        </span>
                                    </div>
                                    <div className="stat-pill" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                        <span className="stat-label">{t('wallet.solde')}</span>
                                        <span className="stat-value">{data.solde.toFixed(2)} $</span>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="wallet-chart-container premium-card animate-fade-up delay-2">
                                <div className="chart-header">
                                    <SwitchButton leftLabel={t('wallet.evolution')}
                                                  rightLabel={t('wallet.repartition')}
                                                  onClickLeft={() => setSwitchBtn(true)}
                                                  onClickRight={() => setSwitchBtn(false)}
                                    />
                                </div>
                                {dataGraph && dateGraph && (switchBtn ?
                                    <LineChart className={"wallet-graph"}
                                               data={dataGraph}
                                               labels={dateGraph}
                                               lineColor={data.performance.value>0 ? constants.green : constants.red}
                                    />
                                    :
                                    <div className={"wallet-camembert"}><Camembert data={dataCam}/></div>
                                )}
                            </div>
                            
                                <div className="list-card premium-card list-stocks animate-fade-up delay-3">
                                    <h3>{t('wallet.stocksOwned', {amount: data.actions.length})}</h3>
                                    <div className="list-scrollable">
                                        {data.actions.length > 0 ? (
                                            data.actions.map((item, index) => (
                                                <div key={item.ticker} className="action-item">
                                                    <div className="item-left" onClick={() => goTicker(item.ticker)}>
                                                        <CustomImage style={{width: 36, height: 36, borderRadius: '50%'}} src={getStockLogo(item.ticker)} fallbackText={item.ticker}/>
                                                        <div className="item-info">
                                                            <p className="item-ticker">{item.ticker}</p>
                                                            <p className="item-sub">{(item.price * item.quantity).toFixed(2)} $</p>
                                                        </div>
                                                    </div>
                                                    <div className="item-right" onClick={() => setIsPercent(!isPercent)}>
                                                        <span className="item-perf" style={{color:item.performance.value>0 ? constants.green : constants.red}}>
                                                            {isPercent ? item.performance.percentage : (item.performance.value > 0 ? '+' : '') + item.performance.value.toFixed(2) + " $"}
                                                        </span>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="empty-state">
                                                <span className="empty-icon">📁</span>
                                                <p>{t('wallet.own_nothing')}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="list-card premium-card list-favorites animate-fade-up delay-4">
                                    <h3>{t('wallet.favorites')}</h3>
                                    <div className="list-scrollable">
                                        {data.favoris.length > 0 ? (
                                            data.favoris.map((item, index) => (
                                                <div key={item.ticker} className="fav-compact-item" onClick={() => goTicker(item.ticker)}>
                                                    <CustomImage style={{width: 32, height: 32, borderRadius: '50%'}} src={getStockLogo(item.ticker)} fallbackText={item.ticker}/>
                                                    <div className="fav-compact-info">
                                                        <span className="item-ticker">{item.ticker}</span>
                                                        <span className="item-price">{item.price} $</span>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="empty-state">
                                                <span className="empty-icon">⭐</span>
                                                <p>{t('wallet.fav_nothing')}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                
                                <div className="list-card premium-card list-orders animate-fade-up delay-5">
                                    <h3>{t('wallet.ordersPlaced')}</h3>
                                    <div className="list-scrollable">
                                        {dataAutomation.length !== 0 ? (
                                            dataAutomation.map(item => (
                                                <div key={item.id} className="order-loop" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                                    <div className="d-flex justify-between align-center border-bottom pb-2" style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' }}>
                                                        <div className="d-flex align-center gap-2 order-stock-btn" style={{ gap: '10px', cursor: 'pointer' }} onClick={() => goTicker(item.Ticker)}>
                                                            <CustomImage style={{width: 28, height: 28, borderRadius: '50%'}} src={getStockLogo(item.Ticker)} fallbackText={item.Ticker}/>
                                                            <span style={{fontWeight: 700, fontSize: '1rem', color: '#0f172a'}}>{item.Ticker}</span>
                                                            <span style={{
                                                                background: '#f1f5f9', 
                                                                color: '#475569', 
                                                                padding: '4px 10px', 
                                                                borderRadius: '16px', 
                                                                fontSize: '0.75rem', 
                                                                fontWeight: 600
                                                            }}>
                                                                {item[t("transactionWidget.orderType")]}
                                                            </span>
                                                        </div>
                                                        <DeleteOutlineRoundedIcon 
                                                            titleAccess={t('transactionWidget.deleteOrder')}
                                                            onClick={() => delAutomation(item.id)} 
                                                            sx={{ color: '#ef4444', cursor: 'pointer', transition: 'all 0.2s', '&:hover': { color: '#dc2626', transform: 'scale(1.1)' } }} 
                                                        />
                                                    </div>
                                                    
                                                    <div className="order-details" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                                        {Object.keys(item).map((auto) => {
                                                            if (auto !== "id" && auto !== "Ticker" && auto !== t("transactionWidget.orderType")) {
                                                                let valueColor = '#0f172a';
                                                                if (auto === t("transactionWidget.transactionType")) {
                                                                    valueColor = item[auto] === transactionTypes.BUY ? '#10b981' : '#ef4444';
                                                                }
                                                                return (
                                                                    <div key={auto} style={{ display: 'flex', flexDirection: 'column' }}>
                                                                        <span style={{ fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, marginBottom: '2px' }}>{auto}</span>
                                                                        <span style={{ fontSize: '0.95rem', fontWeight: 600, color: valueColor }}>{item[auto]}</span>
                                                                    </div>
                                                                );
                                                            }
                                                            return null;
                                                        })}
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="empty-state">
                                                <span className="empty-icon">⏳</span>
                                                <p>{t('wallet.order_nothing')}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                
                        </div>

                        <div className="premium-card animate-fade-up delay-5">
                            {historique?.mouvements === undefined ? (
                                <HistoriqueSkeletonTable />
                            ) : (
                                <HistoriqueTable title={t('wallet.historical')} data={historique?.mouvements}/>
                            )}
                        </div>
                    </div>
            }
        </div>
    );
}

export default Wallet;